import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import objectSupport from 'dayjs/plugin/objectSupport';
import PropTypes from 'prop-types';

import ToggleButton from '@material-ui/lab/ToggleButton';

import { Text, BackIcon, NextIcon, IconButton, AvatarGroup, SelectProvider } from '../../index';

import { StyledDayContainer, RowContainer, StyledMeetDataContainer } from './style';

import SessionPreviewCard from './SessionPreviewCard';

import { MONTHS, SESSION_COLORS } from '../../../constant';

dayjs.extend(objectSupport);

const useStyles = makeStyles((theme) => ({
    root: {
        textTransform: 'none',
        borderRadius: '6px',
        color: theme.palette.common.gray3,
        height: '36px',
        width: '72px',
    },
}));

export const Filter = ({ name, onChange, staffOptions, selectedStaff, setSelectedStaff }) => {
    const user = useSelector((state) => state.user);

    const classes = useStyles();

    const [selectedMonth, setSelectedMonth] = useState(dayjs().month());
    const [selectedYear, setSelectedYear] = useState(dayjs().year());
    const [isToday, setIsToday] = useState(false);

    useEffect(() => {
        setSelectedMonth(dayjs().month());
        setSelectedYear(dayjs().year());
    }, []);

    const handleChangeMonth = (value) => {
        setIsToday(false);
        const newMonth = selectedMonth + value;
        if (newMonth < 0) {
            setSelectedMonth(11);
            setSelectedYear(selectedYear - 1);
        } else if (newMonth > 11) {
            setSelectedMonth(0);
            setSelectedYear(selectedYear + 1);
        } else {
            setSelectedMonth(newMonth);
        }
    };

    useEffect(() => {
        if (onChange) {
            onChange({
                isToday,
                selectedMonth,
                selectedYear,
            });
        }
    }, [isToday, selectedMonth, selectedYear]);

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent:
                    user.staff.staff_role.name !== 'Provider' ? 'space-between' : 'flex-start',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <Text $bold $fontSize="18px" style={{ marginRight: '25px' }}>
                    {name}
                </Text>
                <ToggleButton
                    style={{ marginRight: '20px' }}
                    className={classes.root}
                    value="check"
                    selected={isToday}
                    onChange={() => setIsToday(!isToday)}
                >
                    Today
                </ToggleButton>
                {user.staff.staff_role.name !== 'Provider' && (
                    <div style={{ width: '345px' }}>
                        <SelectProvider
                            staffOptions={staffOptions}
                            selectedStaff={selectedStaff}
                            setSelectedStaff={setSelectedStaff}
                            $small
                        />
                    </div>
                )}
            </div>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    columnGap: '15px',
                    width: '250px',
                    justifyContent: 'space-between',
                }}
            >
                <IconButton onClick={() => handleChangeMonth(-1)}>
                    <BackIcon />
                </IconButton>
                <Text $fontSize="16px" $medium>
                    {`${MONTHS[selectedMonth]} ${selectedYear}`}
                </Text>
                <IconButton onClick={() => handleChangeMonth(1)}>
                    <NextIcon fill="black" />
                </IconButton>
            </div>
        </div>
    );
};

Filter.propTypes = {
    name: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    staffOptions: PropTypes.arrayOf(PropTypes.object),
    selectedStaff: PropTypes.string.isRequired,
    setSelectedStaff: PropTypes.func.isRequired,
};

export const DayRow = ({ type, data }) => {
    const history = useHistory();

    /*--------------- Preview ---------------*/
    const rowContainerRef = useRef(null);
    const sessionPreviewRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedMeet, setSelectedMeet] = useState({});

    const handleRedirect = () => {
        history.push({
            pathname: `/provider/session/${selectedMeet.type}/${selectedMeet.id}`,
            state: {
                prevPath: history.location.pathname,
                prevTab: selectedMeet.type === 'group' ? 1 : 2,
            },
        });
    };

    const clear = () => {
        setAnchorEl(null);
        setSelectedMeet({});
    };

    const handleClickMeet = (event, meet) => {
        if (selectedMeet.id === meet.id) {
            return clear();
        }

        const foundIndex = data.meets.findIndex((item) => item.id === meet.id);

        if (foundIndex !== -1) {
            setSelectedMeet(meet);
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClickOutside = (event) => {
        if (rowContainerRef.current && !rowContainerRef.current.contains(event.target)) {
            clear();
        }
    };
    useEffect(() => {
        document.addEventListener('click', handleClickOutside, false);
        return () => {
            document.removeEventListener('click', handleClickOutside, false);
        };
    }, [selectedMeet]);

    return (
        <RowContainer ref={rowContainerRef}>
            <SessionPreviewCard
                anchorEl={anchorEl}
                ref={sessionPreviewRef}
                data={selectedMeet}
                onClick={handleRedirect}
            />
            <div style={{ display: 'flex', alignItems: 'center', height: '38px' }}>
                <StyledDayContainer $backgroundColor={SESSION_COLORS[type]}>
                    <Text $fontSize="18px" $color="white">
                        {dayjs(data?.date).format('DD')}
                    </Text>
                </StyledDayContainer>
                <Text
                    $fontSize="12px"
                    $color="purple"
                    style={{ textTransform: 'uppercase', marginLeft: '10px' }}
                >
                    {dayjs(data?.date).format('MMM, ddd')}
                </Text>
            </div>
            <div>
                {data?.meets.map((meet) => (
                    <div
                        key={meet.id}
                        style={{
                            display: 'grid',
                            alignItems: 'center',
                            gridTemplateColumns: '1fr auto',
                        }}
                    >
                        <StyledMeetDataContainer selected={meet.id === selectedMeet.id}>
                            <Text $fontSize="12px" $color="gray3" $medium>
                                {`${dayjs()
                                    .set({
                                        hour: meet.startTime.split(':')[0],
                                        minute: meet.startTime.split(':')[1],
                                    })
                                    .format('HH.mm')}-${dayjs()
                                    .set({
                                        hour: meet.endTime.split(':')[0],
                                        minute: meet.endTime.split(':')[1],
                                    })
                                    .format('HH.mm')}`}
                            </Text>
                            <Text
                                $fontSize="14px"
                                $ellipsis
                                $lineClamp={1}
                                $medium
                                onClick={(e) => handleClickMeet(e, meet)}
                            >
                                {meet.name}
                            </Text>
                        </StyledMeetDataContainer>
                        <AvatarGroup data={meet.patients} />
                    </div>
                ))}
            </div>
        </RowContainer>
    );
};

DayRow.propTypes = {
    type: PropTypes.string,
    data: PropTypes.object,
    onClick: PropTypes.func,
};
